import { useEffect, useRef } from "react";
interface Agent {
  id: number;
  kind?: string;
  createdAt: string;
  updatedAt?: string | null;
}
interface Options {
  workspaceId: string;
  enabled: boolean;
  agents: Agent[];
  currentId: number | string | null;
  preferredConversationId?: number | null;
  suppressAutoSelection?: boolean;
  loading: boolean;
  select: (id: number) => unknown;
}
/** Keep agent selection workspace-local and default to its most recently updated conversation. */
export function useWorkspaceAgentSelection({
  workspaceId,
  enabled,
  agents,
  currentId,
  preferredConversationId = null,
  suppressAutoSelection = false,
  loading,
  select,
}: Options) {
  const initializedWorkspaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || loading) return;
    const allOrdered = agents
      .filter((a) => a.kind === "agent")
      .sort(
        (a, b) =>
          (b.updatedAt || b.createdAt).localeCompare(
            a.updatedAt || a.createdAt,
          ) || b.id - a.id,
      );
    const key = `ida-agent-${workspaceId}`;
    const selected = allOrdered.find((a) => a.id === Number(currentId));
    if (selected) {
      initializedWorkspaceRef.current = workspaceId;
      localStorage.setItem(key, String(selected.id));
      return;
    }
    if (suppressAutoSelection) return;
    // The inventory is loaded at this point. A current conversation that is
    // absent from the agent list is a stale/plain-chat selection, not an agent
    // choice to preserve; fall through to a valid agent or the newest one.
    if (!allOrdered.length) return;
    const preferred =
      preferredConversationId === null
        ? undefined
        : allOrdered.find((a) => a.id === preferredConversationId);
    const saved = allOrdered.find(
      (agent) => agent.id === Number(localStorage.getItem(key)),
    );
    const target = preferred || saved || allOrdered[0];
    if (
      currentId === null &&
      initializedWorkspaceRef.current === workspaceId &&
      Number(localStorage.getItem(key)) === target.id
    ) {
      return;
    }
    initializedWorkspaceRef.current = workspaceId;
    localStorage.setItem(key, String(target.id));
    void select(target.id);
  }, [
    workspaceId,
    enabled,
    agents,
    currentId,
    preferredConversationId,
    suppressAutoSelection,
    loading,
    select,
  ]);
}
